/* eslint-disable operator-linebreak */
import {
    AreaSeriesPartialOptions, createChart, IChartApi, ISeriesApi, MouseEventHandler, UTCTimestamp
} from 'lightweight-charts';
import React from 'react';

import { CurrencyEnum } from '@search/graphql-typings';
import { formatPrice } from '@search/helpers/src/gql/formatPrice';

export function useChartSimpleArea({
    items,
    setActiveTime,
    chartProps = chartDefaults,
    chartAreaProps = chartAreaSeriesDefaults
}: {
    items: Parameters<ISeriesApi<'Area'>['setData']>[0];
    setActiveTime: Parameters<typeof chartTooltipHandlerCreate>[0]['setActiveTime'];
    chartAreaProps?: typeof chartAreaSeriesDefaults;
    chartProps?: typeof chartDefaults;
}) {
    const plotRef = React.useRef<HTMLDivElement>(null);
    const tooltipRef = React.useRef<HTMLDivElement>(null);
    const ctlRef = React.useRef<{chart: IChartApi; lines: ISeriesApi<'Area'>}>();

    React.useEffect(() => {
        if (! plotRef.current || ! tooltipRef.current) return;

        const chart = createChart(plotRef.current, chartProps);
        const lines = chart.addAreaSeries(chartAreaProps);
        const handler = chartTooltipHandlerCreate({
            container: plotRef.current,
            toolTip: tooltipRef.current,
            lines,
            setActiveTime
        });

        chart.subscribeCrosshairMove(handler);

        ctlRef.current = { chart, lines };

        return () => {
            chart.unsubscribeCrosshairMove(handler);
            chart.remove();
        };
    }, [ ]);

    React.useEffect(() => {
        ctlRef.current?.lines.setData(items);
        ctlRef.current?.chart.timeScale().fitContent();
    }, [ items ]);

    return {
        plotRef,
        tooltipRef
    };
}

export function chartTimestampFromDate(dateOrString: Date | string) {
    const date = dateOrString instanceof Date ? dateOrString : new Date(dateOrString);

    const time = Math.floor(
        (date.getTime() + date.getTimezoneOffset() * 60 * 1000) / 1000
    ) as UTCTimestamp;

    return time;
}

export function chartDateFromTimestamp(timestamp: UTCTimestamp) {
    const time = timestamp * 1000 - new Date().getTimezoneOffset() * 60 * 1000;

    return new Date(time);
}

export const chartTooltipHandlerCreate: (p: {
    container: HTMLElement;
    lines: ISeriesApi<'Area'>;
    toolTip: HTMLElement;
    setActiveTime(time: number): void;
    size?: {x: number; y: number};
    margin?: number;
}) => MouseEventHandler = ({
    container,
    lines,
    toolTip,
    setActiveTime,
    size = { x: 10, y: 24 },
    margin = 0
}) => param => {
    if (param.point === undefined
        || ! param.time
        || param.point.x < 0 || param.point.x > container.clientWidth
        || param.point.y < 0 || param.point.y > container.clientHeight
    ) {
        toolTip.style.display = 'none';
        return;
    }

    toolTip.style.display = 'block';

    const price = param.seriesPrices.get(lines);

    if (! price) return;
    if (typeof price === 'object') return;

    setActiveTime(! param.time || typeof param.time === 'object' ? 0 : param.time);

    const coordinate = lines.priceToCoordinate(price);

    if (! coordinate) return;

    const shiftedCoordinate = Math.max(0, Math.min(container.clientWidth - size.x, param.point.x - 50));

    const coordinateY = coordinate - size.y - margin > 0 ?
        coordinate - size.y - margin :
        Math.max(0, Math.min(container.clientHeight - size.y - margin, coordinate + margin));

    toolTip.style.left = `${shiftedCoordinate}px`;
    toolTip.style.top = `${coordinateY}px`;
};

export const chartDefaults: Parameters<typeof createChart>[1] = {
    width: 384,
    height: 202,
    localization: {
        locale: 'ru-RU'
    },
    leftPriceScale: {
        scaleMargins: {
            top: 0.05,
            bottom: 0.1
        },
        visible: true,
        borderVisible: false
    },
    rightPriceScale: {
        visible: false
    },
    grid: {
        vertLines: {
            visible: true,
            color: '#EAE9F2'
        },
        horzLines: {
            visible: true,
            color: '#EAE9F2'
        }
    },
    layout: {
        backgroundColor: '#fff',
        textColor: '#6E6A81',
        fontSize: 10
    },
    handleScroll: false,
    handleScale: false,
    timeScale: {
        borderVisible: false,
        tickMarkFormatter(time: UTCTimestamp) {
            const date = chartDateFromTimestamp(time);

            return date.toLocaleDateString('ru-RU', {
                month: 'short',
                day: 'numeric'
            });
        }
    },
    crosshair: {
        vertLine: {
            color: '#3216B5',
            labelVisible: false,
            style: 0,
            width: 1
        },
        horzLine: {
            visible: false,
            labelVisible: false
        }
    }
};

export const chartAreaSeriesDefaults: AreaSeriesPartialOptions = {
    topColor: 'rgba(50, 22, 181, 0.2)',
    bottomColor: 'rgba(50, 22, 181, 0.01)',
    lineColor: '#3216B5',
    lineWidth: 1,
    priceFormat: {
        type: 'custom',
        minMove: 0.02,
        formatter(value: number) {
            return formatPrice({
                value,
                currency: CurrencyEnum.Rub,
                inWords: true
            });
        }
    }
};
